import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { useParams, useLocation } from "react-router-dom";

// helpers/services in your project
import { authHeader } from "_helpers";

// pdf.js
import * as pdfjsLib from "pdfjs-dist/build/pdf";
pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

/**
 * LeaveForm
 * รองรับ 3 แหล่งสำหรับ LeaveRequestID:
 * 1) props.leaveRequestId
 * 2) route param: /leaveform/:LeaveRequestID
 * 3) query string: /leaveform?LeaveRequestID=... หรือ ?leaveRequestId=...
 *
 * Props:
 * - leaveRequestId?: string (GUID)
 * - className?: string
 * - style?: React.CSSProperties
 * - allowZoomOver1?: boolean (default false) // ถ้า true จะยอมให้ fitScale > 1 (ขยายเกิน 100%)
 */
const LeaveForm = ({ leaveRequestId, className, style, allowZoomOver1 = false }) => {
    const params = useParams();
    const location = useLocation();

    const resolvedLeaveRequestId = useMemo(() => {
        // 1) from props
        if (leaveRequestId) return leaveRequestId;

        // 2) from route param
        const routeId =
            params?.LeaveRequestID || params?.leaveRequestId || params?.id || null;
        if (routeId) return routeId;

        // 3) from query string
        const qs = new URLSearchParams(location.search || "");
        return (
            qs.get("LeaveRequestID") ||
            qs.get("leaveRequestId") ||
            qs.get("id") ||
            null
        );
    }, [leaveRequestId, params, location.search]);

    const [pdfBytes, setPdfBytes] = useState(null);
    const [pageBitmapPx, setPageBitmapPx] = useState({ width: 0, height: 0 });
    const [fitScale, setFitScale] = useState(1);
    const [loading, setLoading] = useState(false);
    const [errorText, setErrorText] = useState("");

    const canvasRef = useRef(null);
    const containerRef = useRef(null);

    function base64PdfToUint8Array(pdfBase64) {
        const commaIndex = pdfBase64.indexOf(",");
        const pureBase64 =
            commaIndex >= 0 ? pdfBase64.substring(commaIndex + 1) : pdfBase64;

        const binaryString = atob(pureBase64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) bytes[i] = binaryString.charCodeAt(i);
        return bytes;
    }

    // 1) Load PDF by LeaveRequestID
    useEffect(() => {
        if (!resolvedLeaveRequestId) {
            setPdfBytes(null);
            setErrorText("ไม่พบ LeaveRequestID (ส่งผ่าน props หรือ route/query ก็ได้)");
            return;
        }

        let cancelled = false;

        (async () => {
            try {
                setLoading(true);
                setErrorText("");

                const url = `/api/v1/reportview/leaveform1?LeaveRequestID=${encodeURIComponent(
                    resolvedLeaveRequestId
                )}`;

                const res = await axios.get(url, { headers: authHeader() });
                if (cancelled) return;

                const g = res?.data;
                if (!g?.pdfBase64) {
                    setPdfBytes(null);
                    setErrorText("API ไม่ได้ส่ง pdfBase64 กลับมา");
                    return;
                }

                const bytes = base64PdfToUint8Array(g.pdfBase64);
                setPdfBytes(bytes);
            } catch (err) {
                console.error("Load PDF error:", err);
                if (!cancelled) {
                    setPdfBytes(null);
                    setErrorText(err?.message || "โหลด PDF ไม่สำเร็จ");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [resolvedLeaveRequestId]);

    // 2) Render PDF page 1 ลง canvas (full quality)
    useEffect(() => {
        (async () => {
            try {
                if (!pdfBytes || !canvasRef.current) return;

                const loadingTask = pdfjsLib.getDocument({ data: pdfBytes });
                const pdf = await loadingTask.promise;
                const page = await pdf.getPage(1);

                const baseScale = 1.5;
                const viewport = page.getViewport({ scale: baseScale });

                const canvas = canvasRef.current;
                const ctx = canvas.getContext("2d");

                canvas.width = viewport.width;
                canvas.height = viewport.height;

                setPageBitmapPx({ width: viewport.width, height: viewport.height });

                await page.render({ canvasContext: ctx, viewport }).promise;
            } catch (err) {
                console.error("Render PDF error:", err);
                setErrorText(err?.message || "Render PDF ไม่สำเร็จ");
            }
        })();
    }, [pdfBytes]);

    // 3) Fit width เมื่อ resize
    const recalcFitScale = useCallback(() => {
        if (!containerRef.current) return;
        if (!pageBitmapPx.width || !pageBitmapPx.height) return;

        const maxW = containerRef.current.clientWidth || 0;
        if (maxW <= 0) return;

        const scaleW = maxW / pageBitmapPx.width;
        const next = allowZoomOver1 ? scaleW : Math.min(scaleW, 1);

        setFitScale((prev) => (Math.abs(prev - next) > 1e-6 ? next : prev));
    }, [pageBitmapPx.width, pageBitmapPx.height, allowZoomOver1]);

    useEffect(() => {
        const onResize = () => recalcFitScale();
        recalcFitScale();
        window.addEventListener("resize", onResize);
        window.addEventListener("orientationchange", onResize);
        return () => {
            window.removeEventListener("resize", onResize);
            window.removeEventListener("orientationchange", onResize);
        };
    }, [recalcFitScale]);

    return (
        <div
            ref={containerRef}
            className={className}
            style={{
                position: "fixed",
                inset: 0,
                background: "#111",
                overflow: "auto",
                padding: 0,
                margin: 0,
                ...style,
            }}
        >
            {/* Loading / Error Overlay */}
            {(loading || errorText) && (
                <div
                    style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                        padding: "10px 12px",
                        background: "rgba(0,0,0,0.65)",
                        color: "#fff",
                        fontSize: 14,
                        fontFamily: "Sarabun, sans-serif",
                    }}
                >
                    {loading ? "กำลังโหลดเอกสาร..." : `เกิดข้อผิดพลาด: ${errorText}`}
                    {!loading && resolvedLeaveRequestId ? (
                        <div style={{ opacity: 0.8, marginTop: 4 }}>
                            LeaveRequestID: {resolvedLeaveRequestId}
                        </div>
                    ) : null}
                </div>
            )}

            {/* Centered Frame */}
            <div
                style={{
                    width: pageBitmapPx.width ? pageBitmapPx.width * fitScale : "100%",
                    height: pageBitmapPx.height ? pageBitmapPx.height * fitScale : "auto",
                    margin: "0 auto",
                    background: "#fff",
                }}
            >
                <canvas
                    ref={canvasRef}
                    style={{
                        display: "block",
                        width: "100%",
                        height: "100%",
                        background: "#fff",
                    }}
                />
            </div>
        </div>
    );
};

export default LeaveForm;
